/**
 * Cart "edit variant" (pencil) — correct, race-free replacement.
 *
 * The stock wpbingo flow was:
 *   POST /cart/add.js  ->  ShopifyAPI.changeItem(line + 1, 0)   [fire and forget]
 *                      ->  setTimeout(ajaxCart(Page).load(), 300)
 * Two defects:
 *   1. The re-render ran on a fixed 300ms timer, not after the removal resolved.
 *      When /cart/change.js was slower than that, /cart.js still contained BOTH
 *      lines, so the cart rendered the item twice with a doubled total and never
 *      re-rendered. Removing the stale row then hit a line index that no longer
 *      existed ("Cannot find variant").
 *   2. `line + 1` assumed the add ALWAYS prepends a brand new line. It does not:
 *      if the chosen variant already exists in the cart, Shopify merges into that
 *      line and nothing shifts, so `line + 1` deleted the wrong item.
 *
 * This version awaits every request and only then re-renders, and it re-reads
 * the cart after the add to locate the original line by identity (variant +
 * properties) rather than by arithmetic on a stale index. Note that a line-item
 * `key` is NOT usable here: /cart/add.js rotates the key hash of every existing
 * line, so a key captured before the add is rejected afterwards with
 * "no valid id or line parameter" (verified against the live cart).
 *
 * It also carries the original line's properties (e.g. Gift Message) onto the
 * replacement, and treats a same-variant submit as a quantity change instead of
 * an add + delete.
 */
(function (window) {
  'use strict';

  var FORM = '#form-edit_variants-addtocart';
  var BTN = FORM + ' .btn--add-to-cart';

  function $() {
    return window.jQuery.apply(null, arguments);
  }

  function getJSON(url) {
    return fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error(url);
      return r.json();
    });
  }

  // Stable identity for a cart line that survives an add (unlike `key`).
  function lineSig(item) {
    var props = item.properties || {};
    var keys = Object.keys(props).sort();
    var parts = keys.map(function (k) {
      return k + '=' + props[k];
    });
    return item.variant_id + '|' + parts.join(' ');
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw data;
        return data;
      });
    });
  }

  function reloadCarts() {
    if (document.body.classList.contains('drawer--open')) {
      window.ajOpenDrawerAfterCartLoad = true;
    }
    if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
      window.ajaxCart.load();
    }
    if (
      document.body.classList.contains('template-cart') &&
      window.ajaxCartPage &&
      typeof window.ajaxCartPage.load === 'function'
    ) {
      window.ajaxCartPage.load();
    }
  }

  function closePanel(panelSel) {
    var narrow = window.matchMedia('(max-width: 766px)').matches;
    $(panelSel).removeClass('is-visible');
    if (narrow) {
      $('body').removeClass('editv_overlay-layer');
      setTimeout(function () {
        $('[data-edit_variant-cart-container]').empty();
      }, 300);
    } else {
      setTimeout(function () {
        $(panelSel).removeClass('transform');
      }, 500);
      setTimeout(function () {
        $('body').removeClass('editv_overlay-layer');
        $('[data-edit_variant-cart-container]').empty();
      }, 700);
    }
  }

  /**
   * @param {jQuery} $parent  the .product-edit_variants wrapper (carries data-line)
   * @param {HTMLFormElement} formEl  the edit-variant add-to-cart form
   * @param {string} panelSel  selector for the slide-in panel
   */
  window.ajEditVariantApply = function ($parent, formEl, panelSel) {
    var $btn = $(BTN);
    var oldLine = parseInt($parent.data('line'), 10);
    var fd = new FormData(formEl);
    var newId = parseInt(fd.get('id'), 10);
    var newQty = parseInt(fd.get('quantity'), 10);
    if (!newQty || newQty < 1) newQty = 1;

    function done() {
      $btn.removeClass('active').addClass('added');
      setTimeout(function () {
        $(BTN).removeClass('added');
      }, 2000);
      reloadCarts();
      setTimeout(function () {
        closePanel(panelSel);
      }, 500);
    }

    function fail(err) {
      $btn.removeClass('active');
      // Cart drifted (another tab, an app, an expired line). A fresh render is
      // the honest recovery — never leave a phantom row on screen.
      reloadCarts();
      if (err && err.description) {
        window.alert(err.description);
      }
    }

    getJSON('/cart.js')
      .then(function (cart) {
        var old = isNaN(oldLine) ? null : cart.items[oldLine - 1];
        if (!old) throw { description: 'This item is no longer in your cart.' };

        if (old.variant_id === newId) {
          if (old.quantity === newQty) return null;
          // No add happens here, so the key is still valid.
          return postJSON('/cart/change.js', { id: old.key, quantity: newQty });
        }

        // Carry the original line's properties onto the replacement.
        var props = old.properties || {};
        Object.keys(props).forEach(function (k) {
          if (props[k] != null && props[k] !== '') {
            fd.append('properties[' + k + ']', props[k]);
          }
        });
        var sig = lineSig(old);

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          body: fd
        })
          .then(function (r) {
            return r.json().then(function (data) {
              if (!r.ok) throw data;
              return data;
            });
          })
          .then(function () {
            // Re-read: the add may have prepended a line (shifting every index)
            // or merged into an existing one (shifting nothing), and it rotated
            // every line key. Identity is the only thing that survived.
            return getJSON('/cart.js');
          })
          .then(function (fresh) {
            for (var i = 0; i < fresh.items.length; i++) {
              if (lineSig(fresh.items[i]) === sig) {
                return postJSON('/cart/change.js', { line: i + 1, quantity: 0 });
              }
            }
            return null;
          });
      })
      .then(done, fail);
  };
})(this);
